import mermaid from "mermaid";
import type {
  DiagramFamily,
  ParseDiagnostic,
  RenderResult,
} from "../../core/contracts";
import type { MermaidPort } from "../../ports/editor-ports";

export interface MermaidEngine {
  initialize(configuration: Record<string, unknown>): void;
  parse(
    source: string,
    options?: { suppressErrors?: boolean },
  ): Promise<{ diagramType: string } | false>;
  render(id: string, source: string): Promise<{ svg: string }>;
}

let renderSequence = 0;

const MERMAID_CONFIGURATION = {
  startOnLoad: false,
  securityLevel: "strict",
  suppressErrorRendering: true,
  deterministicIds: true,
  deterministicIDSeed: "mermaid-designer",
  theme: "base",
  themeVariables: {
    background: "#ffffff",
    primaryColor: "#e9e5ff",
    primaryTextColor: "#27233a",
    primaryBorderColor: "#7b6ee6",
    secondaryColor: "#dff4ee",
    secondaryTextColor: "#23372f",
    secondaryBorderColor: "#72ab98",
    tertiaryColor: "#fff2c7",
    tertiaryTextColor: "#3d3520",
    tertiaryBorderColor: "#c7a956",
    lineColor: "#777386",
    textColor: "#2f2b38",
    mainBkg: "#e9e5ff",
    secondBkg: "#dff4ee",
    nodeBorder: "#7b6ee6",
    clusterBkg: "#edf4ff",
    clusterBorder: "#86a8d8",
    actorBkg: "#edf4ff",
    actorBorder: "#789bc9",
    actorTextColor: "#273347",
    actorLineColor: "#9aa9bd",
    signalColor: "#666177",
    signalTextColor: "#2f2b38",
    labelBoxBkgColor: "#fff2c7",
    labelBoxBorderColor: "#c7a956",
    labelTextColor: "#3d3520",
    noteBkgColor: "#fff7d9",
    noteBorderColor: "#c7a956",
    noteTextColor: "#3d3520",
    activationBkgColor: "#dff4ee",
    activationBorderColor: "#72ab98",
  },
  look: "classic",
  htmlLabels: false,
  fontFamily:
    'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  flowchart: { htmlLabels: false, useMaxWidth: true },
} as const;

const familyFromDiagramType = (diagramType: string): DiagramFamily | null => {
  const normalized = diagramType.toLowerCase();
  if (normalized.startsWith("flowchart")) {
    return "flowchart";
  }
  if (normalized.startsWith("sequence")) {
    return "sequence";
  }
  if (normalized.startsWith("state")) {
    return "state";
  }
  if (normalized === "er") {
    return "er";
  }
  if (normalized.startsWith("class")) {
    return "class";
  }
  return null;
};

const unmatched = (value: string, open: string, close: string) =>
  value.split(open).length !== value.split(close).length;

const gentleHint = (source: string, line: number | null) => {
  const sourceLine = line ? source.split("\n")[line - 1]?.trim() ?? "" : "";
  if (/[\]\)}][A-Za-z0-9_]/.test(sourceLine)) {
    return "Add a connector before the text after the node, or remove that text.";
  }
  if (/(?:-->|---|-.->|==>)\s*$/.test(sourceLine)) {
    return "Add a node or label after the connector.";
  }
  if ([...["[]", "()", "{}"]].some(([open, close]) => unmatched(sourceLine, open, close))) {
    return "Check that every opening bracket has a matching closing bracket.";
  }
  return "Check the syntax near this line, then try again.";
};

const diagnosticFrom = (source: string, error: unknown): ParseDiagnostic => {
  const rawMessage = error instanceof Error ? error.message : String(error);
  const lineMatch = rawMessage.match(/(?:parse error on line|line)\s+(\d+)/i);
  const parsedLine = lineMatch ? Number(lineMatch[1]) : null;
  const sourceLines = source.split("\n");
  const lastContentLine = Math.max(1, sourceLines.reduce((last, value, index) => value.trim() ? index + 1 : last, 0));
  const line = parsedLine ? Math.min(parsedLine, lastContentLine) : null;
  return {
    message: gentleHint(source, line),
    line,
    column: null,
  };
};

export class BrowserMermaidPort implements MermaidPort {
  constructor(
    private readonly engine: MermaidEngine = mermaid as unknown as MermaidEngine,
  ) {}

  async parseAndRender(source: string): Promise<RenderResult> {
    if (!source.trim()) {
      return {
        ok: false,
        kind: "invalid-source",
        diagnostics: [
          { message: "Enter Mermaid source to render a diagram.", line: 1, column: 1 },
        ],
      };
    }

    try {
      // Excalidraw's Mermaid importer shares Mermaid's singleton and changes its
      // global configuration. Reassert our rendering boundary for every render.
      this.engine.initialize(MERMAID_CONFIGURATION);
      const parsed = await this.engine.parse(source);
      if (!parsed) {
        throw new Error("Mermaid could not parse this source.");
      }
      const diagramFamily = familyFromDiagramType(parsed.diagramType);
      if (!diagramFamily) {
        return {
          ok: false,
          kind: "unsupported",
          diagnostics: [
            {
              message: `Diagram type “${parsed.diagramType}” is not yet supported by Personal V1. Your source is unchanged.`,
              line: 1,
              column: 1,
            },
          ],
        };
      }

      renderSequence += 1;
      const rendered = await this.engine.render(`mermaid-designer-${renderSequence}`, source);
      return { ok: true, diagramFamily, svg: rendered.svg, warnings: [] };
    } catch (error) {
      return { ok: false, kind: "invalid-source", diagnostics: [diagnosticFrom(source, error)] };
    }
  }
}
